import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Form, Modal } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { colors } from '../../brand-colors';

/**
 * AI Visibility Check - Standalone page
 * Check how visible you are to AI platforms
 */
function VisibilityCheck() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileInfo, setProfileInfo] = useState(null);
  const [visibilityResults, setVisibilityResults] = useState(null);
  const [visibilityHistory, setVisibilityHistory] = useState([]);

  // Correction/flagging state
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    search_type: '',
    incorrect_text: '',
    correction_type: 'not_me',
    correction_note: ''
  });
  const [corrections, setCorrections] = useState([]);
  const [savingCorrection, setSavingCorrection] = useState(false);

  useEffect(() => {
    loadProfileInfo();
    loadHistory();
    loadCorrections();
  }, []);

  const loadProfileInfo = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/profile-preview', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setProfileInfo(data.profile);
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/visibility-history', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.history.length > 0) {
          setVisibilityHistory(data.history);
          // Show latest result
          const latest = data.history[0];
          setVisibilityResults({
            results: {
              name: { query: latest.name_query, result: latest.name_result },
              company: { query: latest.company_query, result: latest.company_result },
              industry: { query: latest.industry_query, result: latest.industry_result }
            },
            analysis: {
              name_status: latest.name_status,
              company_status: latest.company_status,
              industry_status: latest.industry_status,
              listed_among_experts: latest.listed_among_experts,
              overall_score: latest.overall_score,
              summary: latest.analysis_summary
            }
          });
        }
      }
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const loadCorrections = async () => {
    try {
      const response = await fetch('/api/authority-engineer/audit/corrections', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCorrections(data.corrections);
        }
      }
    } catch (err) {
      console.error('Error loading corrections:', err);
    }
  };

  const runVisibilityCheck = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/visibility-check', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setVisibilityResults(data);
        loadHistory(); // Refresh history
      } else {
        setError(data.error || 'Failed to run visibility check');
      }
    } catch (err) {
      setError('Failed to run visibility check. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCorrectionModal = (searchType) => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();

    setCorrectionData({
      search_type: searchType,
      incorrect_text: selectedText || '',
      correction_type: 'not_me',
      correction_note: ''
    });
    setShowCorrectionModal(true);
  };

  const saveCorrection = async () => {
    if (!correctionData.incorrect_text.trim()) {
      setError('Please select or enter the incorrect text');
      return;
    }

    setSavingCorrection(true);
    try {
      const response = await fetch('/api/authority-engineer/audit/corrections', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(correctionData)
      });

      const data = await response.json();
      if (data.success) {
        setShowCorrectionModal(false);
        loadCorrections();
        setCorrectionData({
          search_type: '',
          incorrect_text: '',
          correction_type: 'not_me',
          correction_note: ''
        });
      } else {
        setError(data.error || 'Failed to save correction');
      }
    } catch (err) {
      setError('Failed to save correction');
    } finally {
      setSavingCorrection(false);
    }
  };

  const getCorrectionsForType = (searchType) => {
    return corrections.filter(c => c.search_type === searchType);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'not_found': 'secondary',
      'mentioned': 'warning',
      'cited': 'success',
      'expert_listed': 'primary'
    };
    const labels = {
      'not_found': 'Not Found',
      'mentioned': 'Mentioned',
      'cited': 'Cited as Expert',
      'expert_listed': 'Listed as Expert'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          ← Back to Authority Engineer
        </Link>
        <h1>AI Visibility Check</h1>
        <p className="text-muted">See how visible you are to AI platforms like ChatGPT, Claude, and Grok</p>
      </div>

      <Row>
        <Col lg={8}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

          {/* Profile Info Card */}
          <Card className="shadow mb-4">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">Your Search Profile</h5>
            </Card.Header>
            <Card.Body>
              {profileInfo ? (
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1"><strong>Name:</strong> {profileInfo.name || 'Not set'}</p>
                    <p className="mb-1"><strong>Business:</strong> {profileInfo.business || 'Not set'}</p>
                    <p className="mb-0"><strong>Industry:</strong> {profileInfo.industry || 'Not set'}</p>
                  </div>
                  <div>
                    <Button
                      size="lg"
                      style={{ backgroundColor: colors.goldPrimary, border: 'none', color: colors.greyDarkest }}
                      onClick={runVisibilityCheck}
                      disabled={loading}
                    >
                      {loading ? (
                        <><Spinner size="sm" className="me-2" />Searching AI...</>
                      ) : (
                        visibilityResults ? 'Check Again' : 'Check My Visibility'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading profile...
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Results */}
          {visibilityResults && (
            <Card className="shadow">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Visibility Results</h5>
                  <Badge bg={visibilityResults.analysis.overall_score >= 50 ? 'success' : 'warning'}>
                    Score: {visibilityResults.analysis.overall_score}/100
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {/* Status Summary */}
                <Row className="mb-4">
                  <Col md={4} className="text-center">
                    <h6>Your Name</h6>
                    {getStatusBadge(visibilityResults.analysis.name_status)}
                  </Col>
                  <Col md={4} className="text-center">
                    <h6>Your Company</h6>
                    {getStatusBadge(visibilityResults.analysis.company_status)}
                  </Col>
                  <Col md={4} className="text-center">
                    <h6>Industry Expert</h6>
                    {visibilityResults.analysis.listed_among_experts ? (
                      <Badge bg="success">Listed!</Badge>
                    ) : (
                      <Badge bg="secondary">Not Listed</Badge>
                    )}
                  </Col>
                </Row>

                <Alert variant={visibilityResults.analysis.overall_score >= 50 ? 'success' : 'info'}>
                  <strong>Summary:</strong> {visibilityResults.analysis.summary}
                </Alert>

                {/* Detailed Results */}
                <h5 className="mt-4 mb-3">Detailed Results:</h5>

                {visibilityResults.results.name && (
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <Card.Body className="py-3 px-4">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                          Query: "{visibilityResults.results.name.query}"
                        </strong>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => openCorrectionModal('name')}
                        >
                          Flag Incorrect
                        </Button>
                      </div>
                      {getCorrectionsForType('name').length > 0 && (
                        <Alert variant="info" className="py-2 mb-2">
                          <strong>You flagged:</strong> {getCorrectionsForType('name').length} item(s) as incorrect
                        </Alert>
                      )}
                      <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                        <ReactMarkdown>{visibilityResults.results.name.result}</ReactMarkdown>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {visibilityResults.results.company && visibilityResults.results.company.query && (
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <Card.Body className="py-3 px-4">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                          Query: "{visibilityResults.results.company.query}"
                        </strong>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => openCorrectionModal('company')}
                        >
                          Flag Incorrect
                        </Button>
                      </div>
                      {getCorrectionsForType('company').length > 0 && (
                        <Alert variant="info" className="py-2 mb-2">
                          <strong>You flagged:</strong> {getCorrectionsForType('company').length} item(s) as incorrect
                        </Alert>
                      )}
                      <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                        <ReactMarkdown>{visibilityResults.results.company.result}</ReactMarkdown>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {visibilityResults.results.industry && visibilityResults.results.industry.query && (
                  <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <Card.Body className="py-3 px-4">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="text-primary" style={{ fontSize: '1.1rem' }}>
                          Query: "{visibilityResults.results.industry.query}"
                        </strong>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => openCorrectionModal('industry')}
                        >
                          Flag Incorrect
                        </Button>
                      </div>
                      {getCorrectionsForType('industry').length > 0 && (
                        <Alert variant="info" className="py-2 mb-2">
                          <strong>You flagged:</strong> {getCorrectionsForType('industry').length} item(s) as incorrect
                        </Alert>
                      )}
                      <div className="markdown-content" style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                        <ReactMarkdown>{visibilityResults.results.industry.result}</ReactMarkdown>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* History */}
          {visibilityHistory.length > 0 && (
            <Card className="shadow mb-4">
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <h6 className="mb-0">Check History</h6>
              </Card.Header>
              <Card.Body>
                {visibilityHistory.slice(0, 5).map((check, idx) => (
                  <div key={check.id} className={`py-2 ${idx > 0 ? 'border-top' : ''}`}>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-muted">{check.date_formatted}</small>
                      <Badge bg={check.overall_score >= 50 ? 'success' : 'secondary'}>
                        {check.overall_score}/100
                      </Badge>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Quick Links */}
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h6 className="mb-0">Related Tools</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Link to="/command-center/authority-engineer/inventory" className="btn btn-outline-dark">
                  IP Inventory
                </Link>
                <Link to="/command-center/authority-engineer/gaps" className="btn btn-outline-dark">
                  Gap Analysis
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Correction Modal */}
      <Modal show={showCorrectionModal} onHide={() => setShowCorrectionModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
          <Modal.Title>
            <span className="me-2">Flag Incorrect Information</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Select or paste the text that is incorrect. We'll track this and notify you when AI updates their information about you.
          </p>

          <Form.Group className="mb-3">
            <Form.Label><strong>What's wrong?</strong></Form.Label>
            <Form.Select
              value={correctionData.correction_type}
              onChange={(e) => setCorrectionData({ ...correctionData, correction_type: e.target.value })}
            >
              <option value="not_me">This is not me / wrong person</option>
              <option value="wrong_info">Information is incorrect</option>
              <option value="outdated">Information is outdated</option>
              <option value="other">Other issue</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Incorrect Text</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Paste or type the incorrect text here..."
              value={correctionData.incorrect_text}
              onChange={(e) => setCorrectionData({ ...correctionData, incorrect_text: e.target.value })}
            />
            <Form.Text className="text-muted">
              Tip: Select text in the results before clicking "Flag Incorrect" to auto-fill this field
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>Notes</strong> <span className="text-muted">(optional)</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="What should the correct information be?"
              value={correctionData.correction_note}
              onChange={(e) => setCorrectionData({ ...correctionData, correction_note: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCorrectionModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={saveCorrection}
            disabled={savingCorrection || !correctionData.incorrect_text.trim()}
          >
            {savingCorrection ? 'Saving...' : 'Flag for Monitoring'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default VisibilityCheck;
